'use client';

import { useEffect, useRef } from 'react';

// Type for indicator position percentage (-100 to 100)
type IndicatorPosition = number & { readonly __brand: 'IndicatorPosition' };

// Helper function to create a valid indicator position
const createIndicatorPosition = (value: number): IndicatorPosition => {
  if (value < -100 || value > 100) {
    throw new Error('Indicator position must be between -100 and 100');
  }
  return value as IndicatorPosition;
};

// Type guard to check if a number is a valid indicator position
const isValidIndicatorPosition = (value: number): value is IndicatorPosition => {
  return value >= -100 && value <= 100;
};

// Export the helper functions for use in parent components
export { createIndicatorPosition, isValidIndicatorPosition };
export type { IndicatorPosition };

export interface ArcValue {
  /** Percentage value to display on the arc */
  percentage: number;
  /** Optional value to display on the outer arc (e.g., "500PV") */
  label?: string;
  /** Color for this specific arc (falls back to arcColor) */
  color?: string;
  /** Whether to show a border for this specific arc (falls back to showArcBorder) */
  showBorder?: boolean;
  /** Border color for this specific arc (falls back to arcBorderColor) */
  borderColor?: string;
  /** Border width for this specific arc (falls back to arcBorderWidth) */
  borderWidth?: number;
  /** Text color for the percentage on this specific arc (falls back to percentageTextColor) */
  percentageTextColor?: string;
  /** Text color for the label on this specific arc (falls back to labelTextColor) */
  labelTextColor?: string;
  /** Whether this arc should show the position indicator */
  showPositionIndicator?: boolean;
  /** Color of the position indicator for this arc */
  indicatorColor?: string;
  /** Size of the position indicator for this arc */
  indicatorSize?: number;
  /** Position of the indicator as percentage (-100=full left, 0=center, 100=full right) */
  indicatorPosition?: IndicatorPosition;
  /** Horizontal offset from arc center (-1 to 1: -1=left edge, 0=center, 1=right edge) */
  indicatorOffsetX?: number;
  /** Vertical offset from arc center (-1 to 1: -1=up, 0=center, 1=down) */
  indicatorOffsetY?: number;
}

export interface RetailComissionProps {
  /** The title displayed above the chart */
  title?: string;
  /** The value displayed in the center of the chart */
  centerValue?: string | number;
  /** Arc values containing percentage and optional label */
  arcValues?: ArcValue[];
  /** Default color of the arc segments (used if not specified in arcValues) */
  arcColor?: string;
  /** Width of the arc segments */
  arcStrokeWidth?: number;
  /** Width of the separators between arcs */
  separatorStrokeWidth?: number;
  /** Font size for percentage labels */
  percentageFontSize?: number;
  /** Font size for outer labels */
  labelFontSize?: number;
  /** Font size for center value */
  centerValueFontSize?: number;
  /** Background color of the widget */
  backgroundColor?: string;
  /** Width of the widget container */
  width?: number | string;
  /** Height of the widget container */
  height?: number | string;
  /** Class name for the container */
  className?: string;
  /** Default color of the percentage text inside arcs */
  percentageTextColor?: string;
  /** Color of the label text (PV values) */
  labelTextColor?: string;
  /** Color of the center value text */
  centerValueColor?: string;
  /** Default setting for showing borders around arcs */
  showArcBorder?: boolean;
  /** Default color of the arc borders */
  arcBorderColor?: string;
  /** Default width of the arc borders */
  arcBorderWidth?: number;
  /** Return only the SVG element without the container */
  svgOnly?: boolean;
  showPercentageMark?: boolean;
  /** Offset angle for Tier 1 to prevent it from being cut off (in degrees) */
  tier1Offset?: number;
  /** Horizontal offset for the entire gauge (shifts the viewBox) */
  horizontalOffset?: number;
}

const RetailComission = (
  {
    centerValue = '',
    arcValues = [
      { percentage: 15, label: '' },
      { percentage: 20, label: '500PV' },
      { percentage: 25, label: '1000PV' },
      { percentage: 30, label: '2000PV' },
    ],
    arcColor = '#154735',
    arcStrokeWidth = 40,
    separatorStrokeWidth = 33,
    percentageFontSize = 24,
    labelFontSize = 24,
    centerValueFontSize = 32,
    backgroundColor = '#f9f9f9',
    width,
    height,
    className = '',
    percentageTextColor = '#ffffff',
    labelTextColor = '#000000',
    centerValueColor = '#333333',
    showArcBorder = false,
    arcBorderColor = '#000000',
    arcBorderWidth = 2,
    svgOnly = false,
    showPercentageMark = true,
    tier1Offset = 0, // Default value of 0 means no offset
    horizontalOffset = 0, // Default value of 0 means no horizontal shift
  }: RetailComissionProps

  // eslint-disable-next-line sonarjs/cognitive-complexity
) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const arcsGroupRef = useRef<SVGGElement>(null);
  const hcGroupRef = useRef<SVGGElement>(null);
  const outerArcsGroupRef = useRef<SVGGElement>(null);

  const isSafari = () => {
    if (typeof window === 'undefined') return false;
    return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  };

  // Detect iPhone 12 Pro specifically
  const isIPhone12Pro = () => {
    if (typeof window === 'undefined') return false;
    const userAgent = navigator.userAgent;
    return (
      (/iPhone/i.test(userAgent) && window.screen.width === 390 && window.screen.height === 844) ||
      (window.innerWidth === 390 && window.innerHeight === 844)
    );
  };

  useEffect(() => {
    const svg = svgRef.current;
    const arcsGroup = arcsGroupRef.current;
    const hcGroup = hcGroupRef.current;
    const outerArcsGroup = outerArcsGroupRef.current;

    if (!svg || !arcsGroup || !hcGroup || !outerArcsGroup) {
      return;
    }

    // Clear previous elements
    clearGroup(arcsGroup);
    clearGroup(hcGroup);
    clearGroup(outerArcsGroup);

    // SVG dimensions for responsive calculations
    const svgWidth = 500;
    const svgHeight = 350;
    const arcCenterX = svgWidth / 2; // Center X position
    const arcCenterY = svgHeight * 1.15; // Position center below the middle for semi-circle
    const arcOuterRadius = Math.min(svgWidth, svgHeight) * 0.65;

    // Add center text element
    addCenterText(hcGroup, arcCenterX, arcCenterY, centerValue, centerValueColor, centerValueFontSize);

    // Constants for arc calculations
    // For a 180-degree semi-circle with 4 visible arcs and 3 invisible arcs
    const visibleArcAngle = 37; // Reduced visible arc size slightly to prevent overlap
    const invisibleArcAngle = visibleArcAngle / 3.3; // Adjusted invisible arc size proportionally

    // Apply tier1Offset to adjust the first visible arc's angle
    const arcAngles = [
      visibleArcAngle + tier1Offset, // First visible arc gets the offset
      invisibleArcAngle,
      visibleArcAngle,
      invisibleArcAngle,
      visibleArcAngle,
      invisibleArcAngle,
      visibleArcAngle - tier1Offset, // Last arc is adjusted to maintain the total angle
    ];

    const visibleArcData: Array<{
      index: number;
      startAngle: number;
      endAngle: number;
      arcValue: ArcValue;
    }> = [];

    // Create arcs
    let angleSoFar = 0;
    for (let i = 0; i < arcAngles.length; i++) {
      const startAngle = angleSoFar;
      const endAngle = angleSoFar + arcAngles[i];
      angleSoFar = endAngle;

      const pathData = createArcPath(arcCenterX, arcCenterY, arcOuterRadius, startAngle, endAngle);

      if (i % 2 === 1) {
        // Invisible arc (gap)
        addInvisibleArc(arcsGroup, pathData);
      } else {
        // Visible arc
        const arcIndex = i / 2;
        const currentArcValue = arcValues[arcIndex % arcValues.length];

        addVisibleArc({
          arcsGroup,
          pathData,
          arcIndex: i,
          currentArcValue,
          arcColor,
          arcStrokeWidth,
          showArcBorder,
          arcBorderColor,
          arcBorderWidth,
          percentageFontSize,
          percentageTextColor,
        });

        visibleArcData.push({
          index: i,
          startAngle,
          endAngle,
          arcValue: currentArcValue,
        });
      }
    }

    // Create dotted half-circle
    const gap = arcOuterRadius * 0.18;
    const hcRadius = arcOuterRadius - gap;
    addDottedHalfCircle(hcGroup, arcCenterX, arcCenterY, hcRadius);

    // Add position indicators for any arcs that have them enabled
    visibleArcData.forEach(arcData => {
      if (arcData.arcValue.showPositionIndicator) {
        addArcPositionIndicator(
          hcGroup,
          arcCenterX,
          arcCenterY,
          hcRadius,
          arcData,
          arcData.arcValue.indicatorColor || '#ff4444',
          arcData.arcValue.indicatorSize || 4
        );
      }
    });

    // Create outer arcs with labels
    const outerArcRadiusPV = arcOuterRadius * 1.2;
    addOuterLabels(
      outerArcsGroup,
      visibleArcData,
      arcCenterX,
      arcCenterY,
      outerArcRadiusPV,
      labelFontSize,
      labelTextColor
    );

    // Adjust vertical position
    adjustVerticalPosition(arcsGroup, hcGroup, outerArcsGroup, svgHeight);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    centerValue,
    arcValues,
    arcColor,
    arcStrokeWidth,
    separatorStrokeWidth,
    percentageFontSize,
    labelFontSize,
    centerValueFontSize,
    percentageTextColor,
    labelTextColor,
    centerValueColor,
    showArcBorder,
    arcBorderColor,
    arcBorderWidth,
    tier1Offset,
    showPercentageMark,
    horizontalOffset,
  ]);

  // Helper functions to improve code organization and reduce complexity
  function clearGroup(group: SVGGElement) {
    while (group.firstChild) {
      group.removeChild(group.firstChild);
    }
  }

  function addCenterText(
    group: SVGGElement,
    x: number,
    y: number,
    value: string | number,
    color: string,
    fontSize: number
  ) {
    // eslint-disable-next-line sonarjs/no-duplicate-string
    const hcText = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    hcText.setAttribute('id', 'hcText');
    hcText.setAttribute('x', x.toString());
    hcText.setAttribute('y', y.toString());
    hcText.setAttribute('fill', color);
    hcText.setAttribute('font-size', fontSize.toString());
    // eslint-disable-next-line sonarjs/no-duplicate-string
    hcText.setAttribute('font-weight', 'bold');
    // eslint-disable-next-line sonarjs/no-duplicate-string
    hcText.setAttribute('text-anchor', 'middle');
    // eslint-disable-next-line sonarjs/no-duplicate-string
    hcText.setAttribute('dominant-baseline', 'middle');
    hcText.textContent = value.toString();
    group.appendChild(hcText);
  }

  function createArcPath(centerX: number, centerY: number, radius: number, startAngle: number, endAngle: number) {
    const startRad = (startAngle * Math.PI) / 180;
    const endRad = (endAngle * Math.PI) / 180;

    const xStart = centerX + radius * Math.cos(Math.PI - startRad);
    const yStart = centerY - radius * Math.sin(Math.PI - startRad);
    const xEnd = centerX + radius * Math.cos(Math.PI - endRad);
    const yEnd = centerY - radius * Math.sin(Math.PI - endRad);

    return `M ${xStart},${yStart} A ${radius},${radius} 0 0 1 ${xEnd},${yEnd}`;
  }

  function addInvisibleArc(group: SVGGElement, pathData: string) {
    const sepPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    sepPath.setAttribute('d', pathData);
    sepPath.setAttribute('fill', 'none');
    sepPath.setAttribute('stroke', 'none');
    // eslint-disable-next-line sonarjs/no-duplicate-string
    sepPath.setAttribute('stroke-width', '0');
    // eslint-disable-next-line sonarjs/no-duplicate-string
    sepPath.setAttribute('stroke-linecap', 'round');
    group.appendChild(sepPath);
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  function addVisibleArc({
    arcsGroup,
    pathData,
    arcIndex,
    currentArcValue,
    arcColor,
    arcStrokeWidth,
    showArcBorder,
    arcBorderColor,
    arcBorderWidth,
    percentageFontSize,
    percentageTextColor,
  }: {
    arcsGroup: SVGGElement;
    pathData: string;
    arcIndex: number;
    currentArcValue: ArcValue;
    arcColor: string;
    arcStrokeWidth: number;
    showArcBorder: boolean;
    arcBorderColor: string;
    arcBorderWidth: number;
    percentageFontSize: number;
    percentageTextColor: string;
  }) {
    // Add white background for the arc
    const sepPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    sepPath.setAttribute('d', pathData);
    sepPath.setAttribute('fill', 'none');
    sepPath.setAttribute('stroke', '#fff');
    sepPath.setAttribute('stroke-width', separatorStrokeWidth.toString());
    sepPath.setAttribute('stroke-linecap', 'round');
    arcsGroup.appendChild(sepPath);

    const currentColor = currentArcValue.color || arcColor;

    // Get individual arc border settings or fall back to global settings
    const shouldShowBorder = currentArcValue.showBorder !== undefined ? currentArcValue.showBorder : showArcBorder;
    const currentBorderColor = currentArcValue.borderColor || arcBorderColor;
    const currentBorderWidth = currentArcValue.borderWidth || arcBorderWidth;

    // Calculate adjusted arc stroke width to compensate for border
    const adjustedArcStrokeWidth = shouldShowBorder
      ? Math.max(arcStrokeWidth - currentBorderWidth * 2, 1) // Ensure minimum width of 1
      : arcStrokeWidth;

    // If border is enabled, create it first (so arc appears inside it)
    if (shouldShowBorder) {
      const borderPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      borderPath.setAttribute('d', pathData);
      borderPath.setAttribute('fill', 'none');
      borderPath.setAttribute('stroke', currentBorderColor);
      borderPath.setAttribute('stroke-width', arcStrokeWidth.toString());
      borderPath.setAttribute('stroke-linecap', 'round');
      arcsGroup.appendChild(borderPath);
    }

    // Create the arc path (on top of border if it exists)
    const arcPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    const arcID = `arcPath${arcIndex}`;
    arcPath.setAttribute('id', arcID);
    arcPath.setAttribute('d', pathData);
    arcPath.setAttribute('fill', 'none');
    arcPath.setAttribute('stroke', currentColor);
    arcPath.setAttribute('stroke-width', adjustedArcStrokeWidth.toString());
    arcPath.setAttribute('stroke-linecap', 'round');
    arcsGroup.appendChild(arcPath);

    // Create text element for percentage
    const textEl = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    // Use the arc-specific text color if provided, otherwise fall back to the default
    const currentTextColor = currentArcValue.percentageTextColor || percentageTextColor;
    textEl.setAttribute('fill', currentTextColor);
    textEl.setAttribute('font-size', percentageFontSize.toString());
    textEl.setAttribute('font-weight', 'lighter');
    textEl.setAttribute('dominant-baseline', 'middle');

    // Create textPath element - FIXED
    const textPath = document.createElementNS('http://www.w3.org/2000/svg', 'textPath');
    textPath.setAttributeNS('http://www.w3.org/1999/xlink', 'href', `#${arcID}`);
    textPath.setAttribute('startOffset', '50%');
    textPath.setAttribute('text-anchor', 'middle');
    textPath.textContent = `${currentArcValue.percentage}${showPercentageMark ? '%' : ''}`;

    // Append textPath to text element
    textEl.appendChild(textPath);
    // Append text element to arcsGroup
    arcsGroup.appendChild(textEl);
  }

  function addDottedHalfCircle(group: SVGGElement, centerX: number, centerY: number, radius: number) {
    // Manual dot placement
    const startAngle = 180;
    const endAngle = 0;
    const numDots = 20; // You can tweak this for density
    const dotRadius = 2.5; // Half of stroke-width for visual match
    const dotColor = '#999999';

    for (let i = 0; i <= numDots; i++) {
      const angle = startAngle + ((endAngle - startAngle) * i) / numDots;
      const rad = (angle * Math.PI) / 180;
      const x = centerX + radius * Math.cos(Math.PI - rad);
      const y = centerY - radius * Math.sin(Math.PI - rad);
      const dot = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      dot.setAttribute('cx', x.toString());
      dot.setAttribute('cy', y.toString());
      dot.setAttribute('r', dotRadius.toString());
      dot.setAttribute('fill', dotColor);
      group.appendChild(dot);
    }
  }

  function addArcPositionIndicator(
    group: SVGGElement,
    centerX: number,
    centerY: number,
    radius: number,
    arcData: {
      index: number;
      startAngle: number;
      endAngle: number;
      arcValue: ArcValue;
    },
    color: string,
    size: number
  ) {
    // Calculate the middle angle of the arc
    const middleAngle = (arcData.startAngle + arcData.endAngle) / 2;

    // Get offset values, prioritizing indicatorPosition over manual offsets
    let offsetX = arcData.arcValue.indicatorOffsetX || 0;
    const offsetY = arcData.arcValue.indicatorOffsetY || 0;

    // Override offsetX based on indicatorPosition prop (percentage-based)
    if (arcData.arcValue.indicatorPosition !== undefined) {
      // Convert percentage (-100 to 100) to offset (-1 to 1)
      // The branded type ensures the value is already within bounds, but we'll add safety check
      const position = arcData.arcValue.indicatorPosition;
      const clampedPosition = Math.max(-100, Math.min(100, position));
      offsetX = clampedPosition / 100; // Convert percentage to decimal (-1 to 1)
    }

    // Calculate arc width for offset calculations
    const arcWidth = Math.abs(arcData.endAngle - arcData.startAngle);

    // Apply horizontal offset: -1 = left edge, 0 = center, 1 = right edge
    const horizontalOffset = offsetX * (arcWidth / 2);
    const adjustedAngle = middleAngle + horizontalOffset;
    const rad = (adjustedAngle * Math.PI) / 180;

    // Calculate base position at the adjusted angle
    let x = centerX + radius * Math.cos(Math.PI - rad);
    let y = centerY - radius * Math.sin(Math.PI - rad);

    // Apply vertical offset: -1 = up (towards center), 1 = down (away from center)
    // Calculate the direction vector from center to the point on the arc
    const directionX = x - centerX;
    const directionY = y - centerY;
    const distance = Math.sqrt(directionX * directionX + directionY * directionY);

    if (distance > 0) {
      // Normalize the direction vector
      const normalizedX = directionX / distance;
      const normalizedY = directionY / distance;

      // Apply vertical offset along the radial direction
      const verticalOffsetDistance = offsetY * 20; // 20 pixels maximum offset
      x += normalizedX * verticalOffsetDistance;
      y += normalizedY * verticalOffsetDistance;
    }

    // Create the indicator with two circles
    // Outer wrapper circle (200% of inner circle size)
    const wrapperIndicator = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    wrapperIndicator.setAttribute('cx', x.toString());
    wrapperIndicator.setAttribute('cy', y.toString());
    wrapperIndicator.setAttribute('r', (size * 2).toString()); // Wrapper circle is 200% of inner circle
    wrapperIndicator.setAttribute('fill', '#082149');
    wrapperIndicator.setAttribute('stroke', 'none');
    group.appendChild(wrapperIndicator);

    // Inner circle (100% of base size)
    const innerIndicator = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    innerIndicator.setAttribute('cx', x.toString());
    innerIndicator.setAttribute('cy', y.toString());
    innerIndicator.setAttribute('r', size.toString()); // Inner circle is 100% of base size
    innerIndicator.setAttribute('fill', '#E5E5E5');
    innerIndicator.setAttribute('stroke', 'none');
    group.appendChild(innerIndicator);
  }

  // Define addOuterLabels function
  function addOuterLabels(
    group: SVGGElement,
    arcData: Array<{ index: number; startAngle: number; endAngle: number; arcValue: ArcValue }>,
    centerX: number,
    centerY: number,
    radius: number,
    fontSize: number,
    defaultLabelTextColor: string
  ) {
    arcData.forEach(({ startAngle, endAngle, arcValue }) => {
      const arcText = arcValue.label || '';
      if (!arcText) return; // Skip if no label

      const pathData = createArcPath(centerX, centerY, radius, startAngle, endAngle);

      const outerArcPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      const outerArcID = `outerArc_${arcValue.percentage}`;
      outerArcPath.setAttribute('id', outerArcID);
      outerArcPath.setAttribute('d', pathData);
      outerArcPath.setAttribute('fill', 'none');
      outerArcPath.setAttribute('stroke', 'none');
      outerArcPath.setAttribute('stroke-width', '0');
      group.appendChild(outerArcPath);

      // Create text element for label
      const textEl = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      // Use the arc-specific label text color if provided, otherwise fall back to the default
      const currentLabelTextColor = arcValue.labelTextColor || defaultLabelTextColor;
      textEl.setAttribute('fill', currentLabelTextColor);
      textEl.setAttribute('font-size', fontSize.toString());
      textEl.setAttribute('font-weight', 'lighter');
      textEl.setAttribute('dominant-baseline', 'central');

      // Create textPath element for outer label - FIXED
      const textPath = document.createElementNS('http://www.w3.org/2000/svg', 'textPath');
      textPath.setAttributeNS('http://www.w3.org/1999/xlink', 'href', `#${outerArcID}`);
      textPath.setAttribute('startOffset', '50%');
      textPath.setAttribute('text-anchor', 'middle');
      textPath.setAttribute('dy', '-5'); // Adjust vertical position to move text up slightly
      textPath.textContent = arcText;

      // Append textPath to text element
      textEl.appendChild(textPath);
      // Append text element to group
      group.appendChild(textEl);
    });
  }

  function adjustVerticalPosition(
    arcsGroup: SVGGElement,
    hcGroup: SVGGElement,
    outerArcsGroup: SVGGElement,
    svgHeight: number
  ) {
    requestAnimationFrame(() => {
      const arcsBBox = arcsGroup.getBBox();
      const hcBBox = hcGroup.getBBox();
      const outerBBox = outerArcsGroup.getBBox();

      const topY = Math.min(arcsBBox.y, hcBBox.y, outerBBox.y);
      const desiredTop = svgHeight * 0.19; // Responsive top position
      const shiftY = desiredTop - topY;

      arcsGroup.setAttribute('transform', `translate(0, ${shiftY})`);
      hcGroup.setAttribute('transform', `translate(0, ${shiftY})`);
      outerArcsGroup.setAttribute('transform', `translate(0, ${shiftY})`);
    });
  }

  // Create the SVG element
  // Return either just the SVG or the full widget

  // iPhone 12 Pro specific handling
  if (isIPhone12Pro()) {
    return (
      <div
        className={`widget-container ${className} !v2-select-none`}
        style={{
          width: '100%',
          maxWidth: '100%',
          height: 200,
          overflow: 'hidden',
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          margin: 0,
          padding: 0,
        }}
      >
        <svg
          ref={svgRef}
          viewBox="-45 10 600 370"
          preserveAspectRatio="xMidYMid meet"
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
          style={{
            width: '100%',
            height: '100%',
            display: 'block',
          }}
        >
          <g ref={arcsGroupRef} id="arcsGroup" />
          <g ref={hcGroupRef} id="hcGroup" />
          <g ref={outerArcsGroupRef} id="outerArcsGroup" />
        </svg>
      </div>
    );
  }

  return isSafari() ? (
    <div
      className={`widget-container ${className} !v2-select-none`}
      style={{
        width: '100%',
        maxWidth: 530,
        height: 215, // tighter height
        overflow: 'hidden', // clip the top arc text padding
        position: 'relative',
        display: 'flex',
        justifyContent: 'flex-end', // push SVG down
        alignItems: 'center',
        flexDirection: 'column',
      }}
    >
      <svg
        ref={svgRef}
        viewBox={`${horizontalOffset} 10 600 360`}
        preserveAspectRatio="xMidYMid meet"
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
        style={{
          width: '100%',
          height: '100%',
          display: 'block',
        }}
      >
        <g ref={arcsGroupRef} id="arcsGroup" />
        <g ref={hcGroupRef} id="hcGroup" />
        <g ref={outerArcsGroupRef} id="outerArcsGroup" />
      </svg>
    </div>
  ) : (
    <div
      className={`widget-container ${className} !v2-select-none`}
      style={{
        height,
        width,
        position: 'relative',
        margin: '0 auto',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
        borderRadius: '4px',
      }}
    >
      <div style={{ width: '100%', height: '100%', position: 'relative' }}>
        <svg
          ref={svgRef}
          className="thc-svg"
          viewBox={`${horizontalOffset} 10 600 370`}
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
          preserveAspectRatio="xMidYMid meet"
          style={{
            width: '100%',
            height: '100%',
            display: 'block',
          }}
        >
          <g ref={arcsGroupRef} id="arcsGroup"></g>
          <g ref={hcGroupRef} id="hcGroup"></g>
          <g ref={outerArcsGroupRef} id="outerArcsGroup"></g>
        </svg>
      </div>
    </div>
  );
};

export default RetailComission;
